import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const categories = [
  { value: "general",       label: "General",       hint: "A note, a thought, anything." },
  { value: "casting",       label: "Casting",       hint: "Auditions · roles · readings." },
  { value: "press",         label: "Press",         hint: "Interviews · features · profiles." },
  { value: "collaboration", label: "Collaboration", hint: "Writing · design · production." },
];

// Per-category autoresponse messages — shown after a successful submit.
const successMessages = {
  general:       "Received. I read every dossier personally and will reply soon.",
  casting:       "Received. Casting submissions are reviewed twice a month — expect a reply within ten days.",
  press:         "Received. Press requests are answered within 48 hours during the BRUTAL DESIGN press cycle.",
  collaboration: "Received. Collaboration briefs go to the top of the pile — I'll write back inside a week.",
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    category: "general",
    company: "",   // honeypot — must remain empty
    website: "",   // honeypot — must remain empty
  });
  const [status, setStatus] = useState({
    state: "idle",
    error: "",
    successCategory: null,
  });
  // Anti-bot: timestamp the form was rendered. Submissions faster than the
  // backend's CONTACT_MIN_FORM_FILL_SEC threshold are rejected.
  const loadedAtRef = useRef(Date.now() / 1000);
  useEffect(() => { loadedAtRef.current = Date.now() / 1000; }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "", successCategory: null });
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        form_loaded_at: loadedAtRef.current,
      });
      setStatus({
        state: "success",
        error: "",
        successCategory: form.category,
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        message: "",
        category: "general",
        company: "",
        website: "",
      });
      loadedAtRef.current = Date.now() / 1000;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join(" ")
            : "Failed to send. Try again.";
      setStatus({ state: "error", error: msg, successCategory: null });
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-40 bg-[#050505] border-t border-white/10"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease }}
            className="md:col-span-5"
          >
            <div className="overline mb-6">
              <span className="accent-bar" />
              05 — Initiate Protocol
            </div>
            <h2
              className="font-display font-light leading-[0.9] tracking-[-0.03em]"
              style={{ fontSize: "clamp(2.6rem, 7vw, 6rem)" }}
            >
              Let's leave a{" "}
              <span className="italic text-[#c01515]">long lasting</span>{" "}
              memory.
            </h2>
            <p className="mt-8 text-[#a3a3a3] leading-relaxed max-w-md">
              Tag the dossier so the right shelf catches it — casting,
              press, collaboration, or just a thought.
            </p>

            <div className="mt-14 space-y-4">
              <div className="flex items-center gap-3">
                <span className="overline w-24">Channel</span>
                <span className="text-sm text-[#f5f5f5]/85">Direct only</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="overline w-24">Locale</span>
                <span className="text-sm text-[#f5f5f5]/85">
                  Guwahati, Assam · India · Remote
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="overline w-24">Status</span>
                <span className="text-sm text-[#c01515]">
                  ● Open for select projects
                </span>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="md:col-span-6 md:col-start-7 space-y-2"
            data-testid="contact-form"
          >
            {/* Anti-bot honeypot: kept out of viewport, off-tab-order, and
                hidden from screen readers. Bots will fill these in; humans
                won't. Backend rejects submissions where they're non-empty. */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label>
                Company (leave blank)
                <input
                  type="text"
                  name="company"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company}
                  onChange={handleChange}
                />
              </label>
              <label>
                Website (leave blank)
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div className="dotted-line" />

            <div className="pt-6">
              <span className="overline block mb-3">
                01 / Reason for writing <span className="text-[#c01515]">*</span>
              </span>
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-2"
                role="radiogroup"
                aria-label="Reason for writing"
              >
                {categories.map((c) => {
                  const active = form.category === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      data-testid={`category-${c.value}`}
                      onClick={() =>
                        setForm({ ...form, category: c.value })
                      }
                      className={`px-3 py-3 text-left border transition-all duration-300 ${
                        active
                          ? "border-[#c01515] bg-[#c01515]/10"
                          : "border-white/10 hover:border-white/30"
                      }`}
                      aria-checked={active}
                      role="radio"
                    >
                      <span
                        className={`font-mono text-[10px] tracking-[0.25em] uppercase block mb-1 ${
                          active ? "text-[#c01515]" : "text-[#a3a3a3]"
                        }`}
                      >
                        {active ? "● " : ""}
                        {c.label}
                      </span>
                      <span className="text-[11px] text-[#a3a3a3] leading-snug block">
                        {c.hint}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block pt-6">
              <span className="overline block mb-1">
                02 / Your name <span className="text-[#c01515]">*</span>
              </span>
              <input
                data-testid="contact-name-input"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Tell me who you are"
                required
                className="input-line"
              />
            </label>
            <label className="block pt-6">
              <span className="overline block mb-1">
                03 / Email <span className="text-[#c01515]">*</span>
              </span>
              <input
                data-testid="contact-email-input"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="A way to reach you"
                required
                className="input-line"
              />
            </label>
            <label className="block pt-6">
              <span className="overline block mb-1">
                04 / Phone <span className="text-[#c01515]">*</span>
              </span>
              <input
                data-testid="contact-phone-input"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="With country code"
                required
                pattern="^[+0-9\s()\-]{4,40}$"
                title="Use digits, spaces, +, -, () — at least 4 characters"
                className="input-line"
              />
            </label>
            <label className="block pt-6">
              <span className="overline block mb-1">
                05 / The Dossier <span className="text-[#c01515]">*</span>
              </span>
              <textarea
                data-testid="contact-message-input"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Idea, project, monologue, mystery…"
                required
                className="input-line"
                rows={5}
              />
            </label>

            <p className="overline pt-6 text-[#a3a3a3]">
              <span className="text-[#c01515]">*</span> All fields required
            </p>

            <div className="pt-6 flex items-center justify-between flex-wrap gap-4">
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={status.state === "loading"}
                className="btn-sharp btn-accent"
              >
                <span>
                  {status.state === "loading"
                    ? "Transmitting…"
                    : "Send dossier"}
                </span>
                <span>→</span>
              </button>

              {status.state === "success" && (
                <span
                  data-testid="contact-success"
                  className="overline text-[#f5f5f5] max-w-md text-right normal-case tracking-normal text-[12px] leading-snug"
                  style={{ letterSpacing: "0.05em" }}
                >
                  ✓ {successMessages[status.successCategory] || successMessages.general}
                </span>
              )}
              {status.state === "error" && (
                <span
                  data-testid="contact-error"
                  className="overline text-[#c01515]"
                >
                  ✗ {status.error}
                </span>
              )}
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
